import { useEffect, useRef, useState } from 'react'
import { usePostHog } from '@posthog/react'
import ContentHeader from '../components/ContentHeader'
import {
  RETIRED_FEATURE_FLAGS,
  loadFeatureFlagOverrides,
  removeRetiredFeatureFlags,
  saveFeatureFlagOverrides,
  type FeatureFlagOverrides,
} from '../utils/featureFlags'
import '../style/views/SettingsView.css'

const STRICT_VALIDATION_KEY = 'scribbl_strict_api_validation'

function readStrictValidationOverride(): string | null {
  return localStorage.getItem(STRICT_VALIDATION_KEY)
}

function readFlagVariants(posthog: ReturnType<typeof usePostHog>): Record<string, boolean> {
  const variants = posthog?.featureFlags?.getFlagVariants?.() ?? {}
  return Object.fromEntries(
    Object.entries(variants)
      .flatMap(([key, value]) => RETIRED_FEATURE_FLAGS.has(key) ? [] : [[key, !!value]]),
  )
}

function FeatureFlagsView() {
  const posthog = usePostHog()
  const [flags, setFlags] = useState<Record<string, boolean>>(() => readFlagVariants(posthog))
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>(loadFeatureFlagOverrides)
  const [strictOverride, setStrictOverride] = useState<string | null>(readStrictValidationOverride)
  const [strictValidation, setStrictValidation] = useState<boolean>(() => {
    const stored = readStrictValidationOverride()
    if (stored === 'true') return true
    if (stored === 'false') return false
    return import.meta.env.DEV
  })

  const mountedRef = useRef(false)

  useEffect(() => {
    if (!posthog) return
    const unsub = posthog.onFeatureFlags(() => setFlags(readFlagVariants(posthog)))
    posthog.reloadFeatureFlags()
    return unsub
  }, [posthog])

  useEffect(() => {
    const activeOverrides = removeRetiredFeatureFlags(overrides)
    if (Object.keys(activeOverrides).length > 0) {
      posthog?.featureFlags?.override(activeOverrides)
    } else if (mountedRef.current) {
      // Only clear PostHog overrides when the user explicitly removes them,
      // not on initial mount (which would log "Flag overrides cleared" and
      // disrupt the flag loading cycle).
      posthog?.featureFlags?.override(false)
    }
    mountedRef.current = true
  }, [overrides, posthog])

  const handleToggle = (key: string) => {
    const currentValue = overrides[key] ?? flags[key] ?? false
    const newOverrides = removeRetiredFeatureFlags({ ...overrides, [key]: !currentValue })
    setOverrides(newOverrides)
    saveFeatureFlagOverrides(newOverrides)
  }

  const handleClearOverride = (key: string) => {
    const newOverrides = { ...overrides }
    delete newOverrides[key]
    setOverrides(newOverrides)
    saveFeatureFlagOverrides(newOverrides)
  }

  const handleClearAll = () => {
    setOverrides({})
    saveFeatureFlagOverrides({})
    posthog?.featureFlags?.override(false)
  }

  const activeOverrides = removeRetiredFeatureFlags(overrides)
  const allKeys = Array.from(new Set([...Object.keys(flags), ...Object.keys(activeOverrides)])).sort()
  const hasOverrides = Object.keys(activeOverrides).length > 0

  return (
    <div className="view-shell">
      <ContentHeader title="Feature Flags" />
      <div className="settings-layout">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1f2937' }}>
                Local Overrides
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                Toggle feature flags for your browser. Overrides are local only and persist across sessions.
              </p>
            </div>
            {hasOverrides && (
              <button type="button" className="primary-outline small" onClick={handleClearAll}>
                Clear all overrides
              </button>
            )}
          </div>

          {allKeys.length === 0 ? (
            <p style={{ fontSize: 14, color: '#6b7280' }}>No feature flags found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {allKeys.map((key) => {
                const isOverridden = key in activeOverrides
                const effectiveValue = isOverridden ? activeOverrides[key] : (flags[key] ?? false)
                const remoteValue = flags[key] ?? false

                return (
                  <div key={key} className="settings-toggle-row">
                    <div style={{ flex: 1 }}>
                      <div className="settings-toggle-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <code style={{ fontSize: 13, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                          {key}
                        </code>
                        {isOverridden && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#f59e0b',
                              background: '#fef3c7',
                              padding: '1px 6px',
                              borderRadius: 4,
                            }}
                          >
                            overridden
                          </span>
                        )}
                      </div>
                      {isOverridden && (
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                          Remote: {remoteValue ? 'on' : 'off'}
                          {' · '}
                          <button
                            type="button"
                            onClick={() => handleClearOverride(key)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: '#6366f1',
                              cursor: 'pointer',
                              fontSize: 12,
                              textDecoration: 'underline',
                            }}
                          >
                            reset to remote
                          </button>
                        </span>
                      )}
                    </div>
                    <div
                      className={`settings-switch ${effectiveValue ? 'on' : ''}`}
                      onClick={() => handleToggle(key)}
                    >
                      <span className="settings-switch-status">{effectiveValue ? 'ON' : 'OFF'}</span>
                      <span className="settings-switch-thumb" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
              Developer Settings
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Local overrides for development and debugging.
            </p>
          </div>

          <div className="settings-toggle-row">
            <div>
              <code style={{ fontSize: 13 }}>Strict API validation</code>
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                {strictOverride !== null ? '(overridden)' : `(default: ${import.meta.env.DEV ? 'ON' : 'OFF'})`}
              </span>
            </div>
            <div
              className={`settings-switch${strictValidation ? ' on' : ''}`}
              onClick={() => {
                const next = !strictValidation
                setStrictValidation(next)
                localStorage.setItem(STRICT_VALIDATION_KEY, String(next))
                setStrictOverride(String(next))
              }}
              role="switch"
              aria-checked={strictValidation}
            >
              <span className="settings-switch-status">{strictValidation ? 'ON' : 'OFF'}</span>
              <span className="settings-switch-thumb" />
            </div>
          </div>

          {strictOverride !== null && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="primary-outline small"
                onClick={() => {
                  localStorage.removeItem(STRICT_VALIDATION_KEY)
                  setStrictOverride(null)
                  setStrictValidation(import.meta.env.DEV)
                }}
              >
                Reset to default
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FeatureFlagsView
